import { Preferences } from "@capacitor/preferences";

/**
 * Force-update gate for installed native builds.
 *
 * The problem it solves: the production API deploys days before the matching
 * binary clears App Review, and old binaries live in the wild indefinitely. If
 * an unavoidable breaking change ships, there is otherwise no way to tell an
 * installed app to stop talking to an API it can no longer understand.
 *
 * FAIL-OPEN IS THE WHOLE DESIGN. Every unknown resolves to "allow": no config,
 * a network error, malformed JSON, a missing platform key. A gate that failed
 * closed would convert any API blip into a total app outage for every user at
 * once, which is a far worse failure than briefly permitting a stale build.
 * The only way a user is ever blocked is an explicit, well-formed instruction
 * to block them.
 */

export interface PlatformConfig {
  minBuild: number;
  recommendedBuild: number;
}

export interface AppConfig {
  ios: PlatformConfig;
  android: PlatformConfig;
}

const CACHE_KEY = "insight:app-config";

/** Shape-checks a parsed payload; anything unexpected becomes null (= allow). */
const parseConfig = (value: unknown): AppConfig | null => {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;

  const platform = (key: "ios" | "android"): PlatformConfig | null => {
    const raw = record[key];
    if (typeof raw !== "object" || raw === null) return null;
    const { minBuild, recommendedBuild } = raw as Record<string, unknown>;
    if (typeof minBuild !== "number" || !Number.isFinite(minBuild)) return null;
    return {
      minBuild,
      recommendedBuild:
        typeof recommendedBuild === "number" &&
        Number.isFinite(recommendedBuild)
          ? recommendedBuild
          : 0,
    };
  };

  const ios = platform("ios");
  const android = platform("android");
  if (ios === null || android === null) return null;
  return { ios, android };
};

/**
 * The only question this module really answers.
 *
 * `<` and never `<=`: minBuild names the OLDEST build still allowed, so a
 * device sitting exactly on it must keep working. Getting this backwards would
 * lock out the very build an operator just declared to be the safe floor.
 */
export const shouldBlock = (
  config: AppConfig | null,
  platform: "ios" | "android",
  build: number,
): boolean => {
  if (config === null) return false;
  const minBuild = config[platform]?.minBuild;
  if (typeof minBuild !== "number" || !Number.isFinite(minBuild)) return false;
  return build < minBuild;
};

export const readCachedConfig = async (): Promise<AppConfig | null> => {
  try {
    const { value } = await Preferences.get({ key: CACHE_KEY });
    if (!value) return null;
    return parseConfig(JSON.parse(value));
  } catch {
    return null;
  }
};

const writeCachedConfig = async (config: AppConfig): Promise<void> => {
  try {
    await Preferences.set({ key: CACHE_KEY, value: JSON.stringify(config) });
  } catch {
    // A device that cannot cache still gates on the live fetch.
  }
};

/**
 * Plain `fetch`, deliberately NOT the shared axios instance.
 *
 * Axios attaches Authorization and the X-App-* headers, none of which the
 * public router's CORS allows, so the request would fail preflight. Worse, its
 * 401 interceptor redirects to sign-in, which must never be triggered by a
 * background config poll.
 */
export const fetchAppConfig = async (
  apiBaseUrl: string,
): Promise<AppConfig | null> => {
  try {
    const res = await fetch(`${apiBaseUrl}/api/public/app-config`, {
      credentials: "omit",
    });
    if (!res.ok) return null;
    const config = parseConfig(await res.json());
    if (config !== null) await writeCachedConfig(config);
    return config;
  } catch {
    return null;
  }
};
